import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Button
} from '@mui/material';
import {
  Security,
  Lock,
  Person,
  ContactSupport,
  ArrowBack,
  Insights,
  Storage,
  CalendarMonth
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { usePageSeo } from './usePageSeo';

const QuickMenuCalendarPrivacyPolicy = () => {
  const navigate = useNavigate();
  usePageSeo({
    title: 'Privacy Policy | Menu Calendar Quick View',
    description: 'Privacy policy for Menu Calendar Quick View, a Mac menu bar calendar by Michael R. Ellis.',
    path: '/menu-calendar-quick-view-privacy-policy'
  });

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Portfolio
        </Button>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Privacy Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Effective Date: September 22, 2026
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            App Name: Menu Calendar Quick View
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Platform: macOS
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Developer: Michael R. Ellis
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Security sx={{ mr: 1 }} />
              Overview
            </Typography>
            <Typography variant="body1" paragraph>
              Menu Calendar Quick View is a macOS menu bar app that shows your calendar, upcoming events, and reminders in a popup next to the system clock. Your calendar and reminder content stays on your Mac and inside Apple&apos;s own frameworks. There is no account to create, no server of ours that receives your events, and we do not sell or share personal information with third parties for advertising.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarMonth sx={{ mr: 1 }} />
              Calendar &amp; Reminders Access
            </Typography>
            <Typography variant="body1" paragraph>
              With your permission, the app reads your calendars, events, and reminders through Apple&apos;s EventKit framework so it can display them, and it writes new events or reminders that you create with its quick-add fields. macOS asks for this permission the first time it is needed, and you can change or revoke it at any time in System Settings under Privacy &amp; Security.
            </Typography>
            <Typography variant="body1" paragraph>
              This content is read on your Mac for display only. Menu Calendar Quick View does not copy your events or reminders to any server we operate, and it does not transmit their titles, times, locations, attendees, or notes off your device. If your calendars sync with iCloud, Google, Exchange, or another provider, that syncing is handled by macOS and by that provider under their own privacy policies, not by this app.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Storage sx={{ mr: 1 }} />
              Information Stored on Your Device
            </Typography>
            <Typography variant="body1" paragraph>
              To provide core features, Menu Calendar Quick View stores the following locally on your Mac using macOS preferences:
            </Typography>
            <Typography component="ul" variant="body1" sx={{ pl: 3 }}>
              <li>Display preferences such as calendar size, view mode, date formats, day shape, week numbers, and weekday headers</li>
              <li>Which of your calendars you have chosen to hide, stored as calendar identifiers</li>
              <li>Feature toggles for events, reminders, the dock icon, and launch at login</li>
              <li>Your chosen global keyboard shortcut for opening the calendar popup</li>
              <li>Interface state such as the selected day, measured popup height, and whether onboarding has been completed</li>
            </Typography>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              These preferences stay on your device. Removing the app removes the data it stores, subject to how macOS manages app deletion.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Insights sx={{ mr: 1 }} />
              Analytics &amp; Crash Reporting
            </Typography>
            <Typography variant="body1" paragraph>
              The app uses Google Firebase Analytics and Firebase Crashlytics to understand general usage patterns and to receive reports when the app crashes. This is the only data the app sends over the network. Firebase may collect standard diagnostic information such as app version, macOS version, device model, approximate region derived from the network connection, a randomly generated app instance identifier, session and app-open events, and — when a crash occurs — a crash stack trace and the state of the app at that moment.
            </Typography>
            <Typography variant="body1" paragraph>
              Your calendar events and reminders are never included in analytics or crash reports. The app does not log event titles, dates, attendees, locations, or notes to these services, and it does not attach your name or email address to them. This information is used only to keep the app stable and to decide what to improve. Firebase processes it as a service provider under{' '}
              <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Google&apos;s privacy and security terms for Firebase
              </a>
              . You can also limit what your Mac shares more broadly under System Settings &gt; Privacy &amp; Security &gt; Analytics &amp; Improvements.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Lock sx={{ mr: 1 }} />
              Information We Do Not Collect
            </Typography>
            <Typography variant="body1" paragraph>
              There is no account registration or login, so we do not collect your name, email address, phone number, or payment details. We do not use advertising networks, and we do not use tracking technologies that follow you across other apps or websites. The app runs inside the macOS App Sandbox and requests only the permissions described above.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Person sx={{ mr: 1 }} />
              Third-Party Links &amp; Content
            </Typography>
            <Typography variant="body1" paragraph>
              The app may link to third-party websites, such as the App Store review page or the developer website. If you follow those links, the privacy practices of those sites apply, not this policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Children&apos;s Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Menu Calendar Quick View is not directed at children under 13 and does not knowingly collect personal information from children. No personal data is required to use its features.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Your Choices &amp; Rights
            </Typography>
            <Typography variant="body1" paragraph>
              You control calendar and reminders access, and you can revoke it at any time in System Settings without uninstalling the app; the app simply stops showing that content. You can change or clear local preferences within the app&apos;s settings, or remove the app to delete the data it stores locally. Because we operate no accounts and no server that holds your data, there is no cloud profile for us to access, export, or delete on your behalf. If you would like a crash report or analytics record associated with your installation deleted, contact us using the address below and include the date and app version so it can be located.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. The effective date at the top of this page will be revised when changes are posted. Continued use of the app after an update constitutes acceptance of the revised policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Related Terms
            </Typography>
            <Typography variant="body1" paragraph>
              Use of Menu Calendar Quick View is also governed by our{' '}
              <RouterLink to="/menu-calendar-quick-view-terms" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Terms and Conditions
              </RouterLink>
              .
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactSupport sx={{ mr: 1 }} />
              Contact
            </Typography>
            <Typography variant="body1" paragraph>
              For privacy questions about Menu Calendar Quick View, contact:{' '}
              <a href="mailto:Contact@MichaelRobertEllis.com" style={{ color: '#1976d2', textDecoration: 'none' }}>
                Contact@MichaelRobertEllis.com
              </a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default QuickMenuCalendarPrivacyPolicy;
